
(() => {
  'use strict';

  const SELECTOR = '.product-colour-variants';
  const CACHE_VERSION = 'pcv-v3';
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const BATCH_SIZE = 4;

  const initialized = new WeakSet();

  function cacheKey(root) {
    return [
      CACHE_VERSION,
      location.host,
      root.dataset.productTitle
    ].join(':');
  }

  function readCache(key) {
    try {
      const cached = JSON.parse(localStorage.getItem(key));

      if (
        cached &&
        Array.isArray(cached.products) &&
        Date.now() - cached.time < CACHE_TTL
      ) {
        return cached.products;
      }
    } catch (_) {}

    return null;
  }

  function writeCache(key, products) {
    try {
      localStorage.setItem(key, JSON.stringify({
        time: Date.now(),
        products
      }));
    } catch (_) {
      // Storage may be unavailable or full.
    }
  }

  function normalizeUrl(url) {
    if (!url) return '';

    try {
      return new URL(url, location.origin).href;
    } catch (_) {
      return '';
    }
  }

  function getCurrentProduct(root) {
    return {
      id: String(root.dataset.productId || ''),
      title: root.dataset.productTitle || '',
      handle: root.dataset.productHandle || '',
      url: root.dataset.productUrl || location.pathname,
      image: root.dataset.currentImage || '',
      color_name:
        root.dataset.currentColorName ||
        root.dataset.originalName ||
        root.dataset.productTitle ||
        '',
      color: root.dataset.currentColor || '',
      color_code: root.dataset.currentColor || ''
    };
  }

  function mergeProducts(products, currentProduct) {
    const unique = new Map();

    // Keep only exact-title matches.
    products.forEach(product => {
      if (
        product &&
        product.title === currentProduct.title &&
        product.id
      ) {
        unique.set(String(product.id), product);
      }
    });

    // Always include the current product.
    // This also replaces a possibly incomplete search result
    // with its authoritative Liquid-provided color and image.
    unique.set(String(currentProduct.id), {
      ...(unique.get(String(currentProduct.id)) || {}),
      ...currentProduct
    });

    return [...unique.values()];
  }

  async function fetchPage(title, page) {
    const rootPath = window.Shopify?.routes?.root || '/';
    const url = new URL(
      `${rootPath}search`,
      location.origin
    );

    url.searchParams.set('view', 'colour-variants');
    url.searchParams.set('q', title);
    url.searchParams.set('type', 'product');
    url.searchParams.set('page', String(page));

    const response = await fetch(url.href, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(
        `Search page ${page} returned ${response.status}`
      );
    }

    return response.json();
  }

  async function fetchAllProducts(title) {
    const firstPage = await fetchPage(title, 1);
    const totalPages = Math.max(
      1,
      Number(firstPage.pages) || 1
    );

    const products = [...(firstPage.products || [])];

    // Fetch remaining pages concurrently in small batches.
    for (
      let start = 2;
      start <= totalPages;
      start += BATCH_SIZE
    ) {
      const pages = Array.from(
        {
          length: Math.min(
            BATCH_SIZE,
            totalPages - start + 1
          )
        },
        (_, index) => start + index
      );

      const results = await Promise.all(
        pages.map(page => fetchPage(title, page))
      );

      results.forEach(data => {
        products.push(...(data.products || []));
      });
    }

    return products;
  }

  function render(root, products) {
    const grid = root.querySelector(
      '.product-colour-variants__grid'
    );

    if (!grid) return;

    const heading = root.querySelector(
      '.product-colour-variants__current-name'
    );

    const currentProduct = getCurrentProduct(root);
    const displayMode = root.dataset.displayMode || 'swatch';

    const maxProducts = Math.max(
      1,
      Number.parseInt(root.dataset.maxProducts, 10) || 12
    );

    const displayedElement = root.querySelector(
      '[data-pcv-displayed]'
    );

    const totalElement = root.querySelector(
      '[data-pcv-total]'
    );

    // Put the current product first, followed by the other matches.
    const orderedProducts = mergeProducts(
      products,
      currentProduct
    );

    const visibleProducts = orderedProducts.slice(
      0,
      maxProducts
    );

    const fragment = document.createDocumentFragment();

    visibleProducts.forEach(product => {
      const colorName =
        product.color_name ||
        product.title ||
        '';

      const color =
        product.color ||
        product.color_code ||
        '';

      const link = document.createElement('a');

      link.href = product.url || '#';

      link.className =
        'product-colour-variants__item' +
        ` product-colour-variants__item--${displayMode}`;

      link.dataset.color = colorName;
      link.title = colorName;
      link.setAttribute('aria-label', colorName);

      const isCurrent =
        String(product.id) === String(currentProduct.id) ||
        product.handle === currentProduct.handle;

      if (isCurrent) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      }

      if (displayMode === 'image') {
        const wrapper = document.createElement('div');
        wrapper.className = 'product-colour-variants__image';

        const imageUrl = normalizeUrl(product.image);

        if (imageUrl) {
          const image = document.createElement('img');

          image.src = imageUrl;
          image.alt = colorName;
          image.loading = 'lazy';

          wrapper.appendChild(image);
        }

        link.appendChild(wrapper);
      } else {
        const swatch = document.createElement('span');

        swatch.className =
          'product-colour-variants__swatch' +
          (color ? '' : ' is-empty');

        if (color) {
          swatch.style.setProperty(
            '--pcv-swatch-color',
            color
          );
        }

        swatch.setAttribute('aria-hidden', 'true');
        link.appendChild(swatch);
      }

      fragment.appendChild(link);
    });

    grid.replaceChildren(fragment);

    if (displayedElement) {
      displayedElement.textContent = String(
        visibleProducts.length
      );
    }

    if (totalElement) {
      totalElement.textContent = String(
        orderedProducts.length
      );
    }

    if (heading && !grid.dataset.pcvEvents) {
      grid.dataset.pcvEvents = 'true';

      const originalName = root.dataset.originalName || '';

      grid.addEventListener('mouseover', event => {
        const item = event.target.closest(
          '.product-colour-variants__item'
        );

        if (!item || !grid.contains(item)) return;
        if (item.contains(event.relatedTarget)) return;

        heading.textContent =
          item.dataset.color || originalName;
      });

      grid.addEventListener('mouseleave', () => {
        heading.textContent = originalName;
      });

      grid.addEventListener('focusin', event => {
        const item = event.target.closest(
          '.product-colour-variants__item'
        );

        if (item && grid.contains(item)) {
          heading.textContent =
            item.dataset.color || originalName;
        }
      });

      grid.addEventListener('focusout', event => {
        if (!grid.contains(event.relatedTarget)) {
          heading.textContent = originalName;
        }
      });
    }
  }

  
  function renderLoader(root) {
  const grid = root.querySelector(
      '.product-colour-variants__grid'
  );

  if (!grid) return;

  const fragment = document.createDocumentFragment();
  const count = 5;

  const loader = document.createElement('div');
  loader.className = 'product-colour-variants__loader';
  loader.setAttribute('aria-hidden', 'true');

  for (let i = 0; i < count; i++) {
      const skeleton = document.createElement('span');

      skeleton.className =
      'product-colour-variants__skeleton';

      loader.appendChild(skeleton);
  }

  fragment.appendChild(loader);
  grid.replaceChildren(fragment);
  }


  async function initialize(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const title = root.dataset.productTitle;
    const grid = root.querySelector(
      '.product-colour-variants__grid'
    );

    if (!title || !grid) return;

    const key = cacheKey(root);
    const currentProduct = getCurrentProduct(root);
    const cached = readCache(key);

    if (cached) {
      render(root, cached);
    } else {
      renderLoader(root);
    }

    try {
      const fetched = await fetchAllProducts(title);

      const completeProducts = mergeProducts(
        fetched,
        currentProduct
      );

      writeCache(key, completeProducts);
      render(root, completeProducts);
    } catch (error) {
      console.error(
        '[Product Colour Variants]',
        error
      );

      // Leave cached results or the current swatch visible.
    }
  }

  function initializeAll() {
    document.querySelectorAll(SELECTOR).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );
})();
